// src/features/apps/StoreAppCheckUtils.ts
//
// Pure rules of the store app id check's Status line (appfactory ADR-0029):
// when the read is asked at all, when a check counts as in flight, how long the
// line keeps re-reading, and which sentence each verdict becomes for an
// operator vs the holder side. The daemon is the authority on the verdict; this
// only decides what is worth showing — and that is NOTHING whenever there is no
// check (no id, no store account that the operator submits from, an older
// daemon, the factory down).

import { DateTime } from 'luxon';
import {
  STORE_APP_CHECK_COPY,
  STORE_APP_CHECK_INFO,
  STORE_APP_CHECK_POLL,
  STORE_APP_CHECK_STATE as S,
  type StoreAppCheckCopyFacts,
  type StoreAppCheckInfo,
} from '@/lib/constants/storeAppCheck';
import type { FactoryStoreAppCheck } from '@/lib/types/AppFactory';

export type StoreAppCheckTone = 'green' | 'red' | 'orange' | 'neutral';

/** The one line under the store's account on the Status tab. */
export interface StoreAppCheckLine {
  tone: StoreAppCheckTone;
  text: string;
  /** RFC 3339 time of the verdict, shown beside a confirmed id. */
  when?: string;
}

/** Who reads the line and what the tab already knows around it. */
export interface StoreAppCheckContext {
  /** false while the operator probe still loads: fail closed, as everywhere. */
  isOperator: boolean;
  /** The Status tab's farm chip reads stale (AppFactoryUtils.farmLiveness). */
  farmStale: boolean;
  copy: StoreAppCheckCopyFacts;
  supportContact: string;
  /** The covering store account's label ('' = none), named to an operator only. */
  accountLabel: string;
}

const VERDICTS: ReadonlySet<string> = new Set([
  S.VERIFIED,
  S.NOT_FOUND,
  S.UNAUTHORIZED,
  S.UNREACHABLE,
]);

export class StoreAppCheckUtils {
  /** The check of a platform, or null when its saved store app id is never checked. */
  static infoFor(platform: string): StoreAppCheckInfo | null {
    return STORE_APP_CHECK_INFO[platform] ?? null;
  }

  /** The read is asked only for a platform that has a check AND a saved id. */
  static shouldAsk(platform: string, storeAppId: string | null | undefined): boolean {
    return StoreAppCheckUtils.infoFor(platform) != null && (storeAppId ?? '').trim() !== '';
  }

  /** A verdict exists (the daemon keeps returning it while a re-check runs). */
  static hasVerdict(res: FactoryStoreAppCheck | null | undefined): boolean {
    return !!res && VERDICTS.has(res.state);
  }

  /**
   * A check is in flight: no verdict yet (`pending`), or a request newer than
   * the last verdict (a re-check). A time that does not parse never counts.
   */
  static isInFlight(res: FactoryStoreAppCheck | null | undefined): boolean {
    if (!res) return false;
    if (res.state === S.PENDING) return true;
    if (!StoreAppCheckUtils.hasVerdict(res)) return false;
    const requested = StoreAppCheckUtils.millis(res.requestedAt);
    if (requested == null) return false;
    const checked = StoreAppCheckUtils.millis(res.checkedAt);
    return checked == null || requested > checked;
  }

  /**
   * The re-read cadence: every INTERVAL_MS while a check is in flight, for at
   * most MAX_MS from the first in-flight read (`firstInFlightAtMs`), then it
   * stops — a later visit of the tab re-reads. false = no re-read.
   */
  static pollInterval(
    inFlight: boolean,
    firstInFlightAtMs: number | null,
    nowMs: number,
  ): number | false {
    if (!inFlight || firstInFlightAtMs == null) return false;
    return nowMs - firstInFlightAtMs < STORE_APP_CHECK_POLL.MAX_MS
      ? STORE_APP_CHECK_POLL.INTERVAL_MS
      : false;
  }

  /**
   * The line, or null = render nothing: a failed read (404 from a daemon older
   * than the check or for a platform without one, 422, 5xx, the network), no
   * answer yet, `none`, `not_applicable` (a holder-submits account therefore
   * sees nothing new) and any state this build does not know.
   */
  static line(
    res: FactoryStoreAppCheck | null | undefined,
    isError: boolean,
    ctx: StoreAppCheckContext,
  ): StoreAppCheckLine | null {
    if (isError || !res) return null;
    const checking: StoreAppCheckLine = { tone: 'neutral', text: STORE_APP_CHECK_COPY.checking(ctx.copy) };
    switch (res.state) {
      case S.PENDING:
        return ctx.farmStale
          ? { tone: 'neutral', text: STORE_APP_CHECK_COPY.farmOffline() }
          : checking;
      case S.VERIFIED:
        return { tone: 'green', text: STORE_APP_CHECK_COPY.verified(ctx.copy), when: res.checkedAt };
      case S.NOT_FOUND:
        return {
          tone: 'red',
          text: `${STORE_APP_CHECK_COPY.notFound(ctx.copy)} ${
            ctx.isOperator
              ? STORE_APP_CHECK_COPY.notFoundOperator(ctx.copy)
              : STORE_APP_CHECK_COPY.notFoundHolder(ctx.supportContact)
          }`,
        };
      case S.UNAUTHORIZED:
        // The account's credentials, not the id: the operator's to fix, never the holder's alarm.
        return ctx.isOperator
          ? { tone: 'orange', text: STORE_APP_CHECK_COPY.unauthorized(ctx.copy, ctx.accountLabel.trim()) }
          : checking;
      case S.UNREACHABLE:
        return { tone: 'neutral', text: STORE_APP_CHECK_COPY.unreachable(ctx.copy) };
      default:
        return null;
    }
  }

  private static millis(iso: string | undefined): number | null {
    if (!iso) return null;
    const dt = DateTime.fromISO(iso);
    return dt.isValid ? dt.toMillis() : null;
  }
}
